import styled from '@emotion/styled'
import React, { useCallback, useState } from 'react'
import { useMutation } from '../../../api/graphql'
import { REFERRAL_SUBMISSION_CREATE_MUTATION } from './chatQueries'

const Wrapper = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const Row = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
`

const Input = styled.input`
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
`

const AddRow = styled.button`
  background: none;
  border: none;
  color: #2563eb;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 12px;

  &:hover {
    text-decoration: underline;
  }
`

const ConsentLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin: 12px 0;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
`

const HelperText = styled.div`
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`

const Submit = styled.button`
  background: #102638;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Dismiss = styled.button`
  background: white;
  color: #6b7280;
  border: 1px solid #d1d5db;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
`

const Status = styled.div<{ ok: boolean }>`
  margin-top: 8px;
  font-size: 13px;
  color: ${props => (props.ok ? '#166534' : '#dc2626')};
`

type CalendarRow = { name: string; url: string }

type Props = {
  supportMessageId: string
  onSubmitted?: () => void
  onDismissed?: () => void
}

const ReferralForm: React.FC<Props> = ({ supportMessageId, onSubmitted, onDismissed }) => {
  const [rows, setRows] = useState<CalendarRow[]>([{ name: '', url: '' }])
  const [notes, setNotes] = useState('')
  const [consent, setConsent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  const [submitReferral] = useMutation(REFERRAL_SUBMISSION_CREATE_MUTATION)

  const updateRow = useCallback((index: number, field: 'name' | 'url', value: string) => {
    setRows(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }, [])

  const addRow = useCallback(() => {
    setRows(prev => [...prev, { name: '', url: '' }])
  }, [])

  const handleSubmit = useCallback(async () => {
    const calendars = rows
      .filter(row => row.name.trim() || row.url.trim())
      .map(row => ({ calendarName: row.name.trim() || null, calendarUrl: row.url.trim() || null }))

    if (calendars.length === 0) {
      setError('Add at least one calendar before submitting.')
      return
    }

    setSubmitting(true)
    setError(null)
    try {
      const result = await submitReferral({
        variables: {
          input: {
            supportMessageId,
            calendars,
            consentToAttribute: consent,
            notes: notes.trim() || null,
          },
        },
      })
      const data = result.data?.referralSubmissionCreate
      const errors = data?.errors
      if (errors && errors.length > 0) {
        setError(errors[0].message)
      } else {
        setSuccess(true)
        if (onSubmitted) onSubmitted()
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }, [rows, notes, consent, submitReferral, supportMessageId, onSubmitted])

  const handleDismiss = useCallback(() => {
    setDismissed(true)
    if (onDismissed) onDismissed()
  }, [onDismissed])

  if (dismissed) return null
  if (success) {
    return (
      <Wrapper>
        <Status ok>Thanks — we'll let you know if anything comes of it.</Status>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {rows.map((row, idx) => (
        <Row key={idx}>
          <Input
            type="text"
            placeholder="Calendar name"
            value={row.name}
            onChange={e => updateRow(idx, 'name', e.target.value)}
          />
          <Input
            type="url"
            placeholder="https://..."
            value={row.url}
            onChange={e => updateRow(idx, 'url', e.target.value)}
          />
        </Row>
      ))}
      <AddRow type="button" onClick={addRow}>
        + Add another calendar
      </AddRow>

      <TextArea
        rows={2}
        placeholder="Anything else worth knowing? (optional)"
        value={notes}
        onChange={e => setNotes(e.target.value)}
      />

      <ConsentLabel>
        <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} />
        <div>
          You can mention my name and email when reaching out.
          <HelperText>
            We'll say something like "Jane suggested we get in touch." We won't claim you endorse us.
          </HelperText>
        </div>
      </ConsentLabel>

      <Actions>
        <Submit type="button" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Sending...' : 'Submit'}
        </Submit>
        <Dismiss type="button" onClick={handleDismiss}>
          No thanks
        </Dismiss>
      </Actions>

      {error && <Status ok={false}>{error}</Status>}
    </Wrapper>
  )
}

export default ReferralForm
